import { createBasePDF, BRAND_COLORS } from '@/utils/pdfConfig';
import { drawHeader } from '@/utils/pdfHeader';
import { drawFooter } from '@/utils/pdfFooter';
import { translateTxType } from '@/utils/dictionary';
import apiClient from '@/api/client';
import { SecureStorage } from '@/utils/secureStorage';

export function useAccountStatement() {

  // Helper para formato de moneda
  const formatMoney = (amount) => {
    return Number(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const user = SecureStorage.getItem('llevo_user');

  // Helper para cargar imagen (Proxy para CORS)
  const loadImage = (url) => {
    return new Promise((resolve) => {
      if (!url) return resolve(null);
      const img = new Image();
      img.setAttribute('crossOrigin', 'anonymous');
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = url;
    });
  };

  const generateStatement = async (dossier) => {
    const nombreUsuario = user?.full_name || 'SISTEMA';
    const { loan, customer, product, transactions, payment_plans, theoretical_plans } = dossier;

    try {
      const configRes = await apiClient.get('/company_config');
      const config = Array.isArray(configRes.data) ? configRes.data[0] : configRes.data;
      const logoImg = await loadImage(config.logo_url);

      // 1. CÁLCULOS DINÁMICOS DEL CRÉDITO
      let cuotaActual = 0;
      let fechaProximoPago = 'N/A';
      let diasAtraso = 0;
      let cuotasVencidas = 0;

      // Totales para la caja de Información de Pago
      let interesNormalPte = 0;
      let interesMoratorioPte = 0;

      if (payment_plans && payment_plans.length > 0) {
        const pendingPlans = payment_plans.filter(p => p.status !== 'PAID');

        // Protección en caso de que theoretical_plans venga vacío o null
        const pendingPlansT = theoretical_plans ? theoretical_plans.filter(p => p.status !== 'PAID') : [];

        // Sumatoria de intereses pendientes de las cuotas no pagadas
        pendingPlans.forEach(p => {
            interesNormalPte += Number(p.interest || 0);
            interesMoratorioPte += Number(p.moratory_interest || p.penalty || 0);
        });

        if (pendingPlansT.length > 0) {
          const oldPlan = pendingPlansT[0];
          cuotaActual = oldPlan?.total_amount || 0;
        }

        if (pendingPlans.length > 0) {
          const nextPlan = pendingPlans[0];

          cuotaActual = nextPlan.total_amount;
          fechaProximoPago = nextPlan.due_date;

          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const nextDate = new Date(nextPlan.due_date + 'T00:00:00');

          if (today > nextDate) {
            const diffTime = Math.abs(today - nextDate);
            diasAtraso = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            cuotasVencidas = pendingPlans.filter(p => {
              const d = new Date(p.due_date + 'T00:00:00');
              return today > d;
            }).length;
          }
        }
      }

      // 2. INICIALIZACIÓN DEL PDF
      const doc = createBasePDF('p', 'letter');
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 15;
      const contentWidth = pageWidth - (margin * 2);

      // --- HEADER GLOBAL ---
      drawHeader(doc, {
        companyName:config.trade_name,
        title: 'ESTADO DE CUENTA',
        subtitle: `Impreso el: ${new Date().toLocaleDateString('es-ES')} a las ${new Date().toLocaleTimeString('es-ES')} | Usuario: ${nombreUsuario}`,
        logo: logoImg,
        useColors: false
      });

      let y = 45;
      if (loan.status === 'REJECTED' || loan.status === 'RECHAZADO') {
        doc.saveGraphicsState(); // Guarda el estado actual (colores, opacidad)

        doc.setFontSize(100);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(200, 0, 0); // Rojo oscuro
        doc.setGState(new doc.GState({ opacity: 0.5 })); // Opacidad baja para no tapar el texto

        // Posicionamiento en el centro con rotación de 45 grados
        doc.text('RECHAZADO', pageWidth   / 1.5, pageHeight-50, {
          align: 'center',
          angle: 45
        });

        doc.restoreGraphicsState(); // Restaura el estado original para el resto del PDF
      }
      // --- 3. GRID DE INFORMACIÓN GENERAL ---
      doc.setFontSize(8.5);
      const col1X = margin;
      const col2X = margin + (contentWidth / 2) + 5;
      const lineH = 5.5;

      const drawLabelValue = (label, value, x, currentY, isHighlight = false) => {
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(30, 30, 30);
        doc.text(label, x, currentY);
        doc.setFont('helvetica', isHighlight ? 'bold' : 'normal');
        doc.setTextColor(0, 0, 0);
        doc.text(value ? String(value) : 'N/A', x + 32, currentY);
      };

      // Columna Izquierda
      drawLabelValue('Crédito:', loan.code, col1X, y);
      drawLabelValue('Cód. Cliente:', customer.identity_document, col1X, y + lineH);
      drawLabelValue('Estado:', loan.status, col1X,y + (lineH * 2));
      drawLabelValue('Cliente:', customer.full_name.toUpperCase(), col1X, y + (lineH * 3));
      drawLabelValue('Monto Desemb:', `$ ${formatMoney(loan.amount)}`, col1X, y + (lineH * 4));
      drawLabelValue('Frecuencia:', loan.payment_frequency?.toUpperCase() || 'MENSUAL', col1X, y + (lineH * 5));
      drawLabelValue('Plazo (Meses):', `${loan.term}`, col1X, y + (lineH * 6));
      drawLabelValue('Valor de Cuota:', `$ ${formatMoney(cuotaActual)}`, col1X, y + (lineH * 7), true);

      // Columna Derecha
      drawLabelValue('Moneda:', 'USD DÓLAR AMERICANO', col2X, y);
      drawLabelValue('Ejecutivo:', loan.executive?.toUpperCase() || 'N/A', col2X, y + lineH);
      drawLabelValue('Producto:', product?.name?.toUpperCase() || 'ESTÁNDAR', col2X, y + (lineH * 2));
      drawLabelValue('Destino:', loan.destination?.toString().toUpperCase() || 'CAPITAL DE TRABAJO', col2X, y + (lineH * 3));
      drawLabelValue('Tasa Interés:', `${loan.rate}% Mensual`, col2X, y + (lineH * 4));
      drawLabelValue('Tasa Mora:', `${loan.moratory_rate || '0'}%`, col2X, y + (lineH * 5));
      drawLabelValue('Próximo Pago:', fechaProximoPago, col2X, y + (lineH * 6), true);

      // Alerta visual de mora
      y += (lineH * 7.5);
      if (diasAtraso > 0) doc.setTextColor(200, 0, 0);
      doc.setFont('helvetica', 'bold');
      doc.text(`Días Atraso: ${diasAtraso}    |    Cuotas Vencidas: ${cuotasVencidas}`, col2X, y - 4);
      doc.setTextColor(0, 0, 0);

      // --- 4. CAJAS DE RESUMEN FINANCIERO ---
      const boxWidth = (contentWidth - 10) / 2;
      const boxHeight = 42;

      doc.setDrawColor(100, 100, 100);
      doc.setLineWidth(0.15);

      // Cálculos para Resumen de Transacciones
      let movDe = '-';
      let movAl = '-';
      let cantPagos = 0;
      if (transactions && transactions.length > 0) {
        const validTx = transactions.filter(t => t.transaction_date);
        if(validTx.length > 0) {
            const dates = validTx.map(t => new Date(t.transaction_date.split(' ')[0]));
            movDe = new Date(Math.min(...dates)).toLocaleDateString('es-ES');
            movAl = new Date(Math.max(...dates)).toLocaleDateString('es-ES');
        }
        cantPagos = transactions.filter(t => t.transaction_type === 'PAYMENT' || t.transaction_type === 'PAGO').length;
      }
      const capitalPagado = loan.amount - loan.remaining_balance;

      // Caja 1: Información de Pago
      doc.roundedRect(col1X, y, boxWidth, boxHeight, 1.5, 1.5);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.text('INFORMACIÓN DE PAGO', col1X + (boxWidth / 2), y + 6, { align: 'center' });
      doc.setDrawColor(220, 220, 220);
      doc.line(col1X, y + 8, col1X + boxWidth, y + 8);

      const saldoAlDia = loan.remaining_balance + interesNormalPte + interesMoratorioPte;

      doc.setFont('helvetica', 'normal');
      doc.text('+ Interés moratorio:', col1X + 4, y + 14);
      doc.text(`$ ${formatMoney(interesMoratorioPte)}`, col1X + boxWidth - 4, y + 14, { align: 'right' });
      doc.text('+ Interés normal:', col1X + 4, y + 19);
      doc.text(`$ ${formatMoney(interesNormalPte)}`, col1X + boxWidth - 4, y + 19, { align: 'right' });
      doc.text('+ Saldo capital:', col1X + 4, y + 24);
      doc.text(`$ ${formatMoney(loan.remaining_balance)}`, col1X + boxWidth - 4, y + 24, { align: 'right' });

      doc.setFont('helvetica', 'bold');
      doc.text('= Saldo al dia:', col1X + 4, y + 31);
      doc.text(`$ ${formatMoney(saldoAlDia)}`, col1X + boxWidth - 4, y + 31, { align: 'right' });

      doc.text('Cancelación total:', col1X + 4, y + 37);
      doc.text(`$ ${formatMoney(saldoAlDia)}`, col1X + boxWidth - 4, y + 37, { align: 'right' });

      // Caja 2: Resumen Transacciones
      doc.setDrawColor(100, 100, 100);
      doc.roundedRect(col2X, y, boxWidth, boxHeight, 1.5, 1.5);
      doc.text('RESUMEN DE TRANSACCIONES', col2X + (boxWidth / 2), y + 6, { align: 'center' });
      doc.setDrawColor(220, 220, 220);
      doc.line(col2X, y + 8, col2X + boxWidth, y + 8);

      doc.setFont('helvetica', 'normal');
      doc.text(`Movimientos de: ${movDe} al ${movAl}`, col2X + 4, y + 14);
      doc.text('Cantidad de pagos realizados:', col2X + 4, y + 19);
      doc.text(`${cantPagos}`, col2X + boxWidth - 4, y + 19, { align: 'right' });

      doc.text('Monto Desembolsado:', col2X + 4, y + 24);
      doc.text(`$ ${formatMoney(loan.amount)}`, col2X + boxWidth - 4, y + 24, { align: 'right' });
      doc.text('- Capital Pagado:', col2X + 4, y + 29);
      doc.text(`$ ${formatMoney(capitalPagado)}`, col2X + boxWidth - 4, y + 29, { align: 'right' });

      doc.setFont('helvetica', 'bold');
      doc.text('= Saldo Capital:', col2X + 4, y + 36);
      doc.text(`$ ${formatMoney(loan.remaining_balance)}`, col2X + boxWidth - 4, y + 36, { align: 'right' });

      y += boxHeight + 10;

      // --- 5. TABLA DE DETALLE DE MOVIMIENTOS ---
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.text('DETALLE DE MOVIMIENTOS', pageWidth / 2, y, { align: 'center' });

      y += 4;
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.3);
      doc.line(margin, y, pageWidth - margin, y);
      doc.line(margin, y + 7, pageWidth - margin, y + 7);

      // ¡AQUÍ ESTÁ LA MAGIA! Declaramos las columnas FUERA del loop para que todo el PDF las reconozca
      const colFecha = margin;
      const colTrans = margin + 15;
      const colTipo = margin + 34;
      const colMonto = margin + 92;
      const colCapital = margin + 111;
      const colInteres = margin + 130;
      const colMora = margin + 148;
      const colCargos = margin + 166;
      const colSaldo = pageWidth - margin;

      const thY = y + 5;
      doc.setFontSize(7.5);
      doc.text('FECHA', colFecha, thY);
      doc.text('N° TRANS.', colTrans, thY);
      doc.text('TIPO', colTipo, thY);
      doc.text('MONTO', colMonto, thY, { align: 'right' });
      doc.text('CAPITAL', colCapital, thY, { align: 'right' });
      doc.text('INTERÉS', colInteres, thY, { align: 'right' });
      doc.text('I. MORA', colMora, thY, { align: 'right' });
      doc.text('CARGOS', colCargos, thY, { align: 'right' });
      doc.text('SALDO', colSaldo, thY, { align: 'right' });

      y += 11;
      doc.setFont('helvetica', 'normal');

      // Variables para los TOTALES
      let totMonto = 0, totCapital = 0, totInteres = 0, totCargos = 0, totMora = 0;

      if (!transactions || transactions.length === 0) {
        doc.text('No hay movimientos registrados.', pageWidth / 2, y + 5, { align: 'center' });
        y += 10;
      } else {
        transactions.forEach((tx) => {
          // Sumatorias
          if(tx.transaction_type=='PAYMENT'){
            totMonto += Number(tx.total_amount || 0);
            totCapital += Number(tx.principal_applied || 0);
            totInteres += Number(tx.interest_applied || 0);
            totMora +=Number(tx.late_fee_applied || 0);
            totCargos += Number(tx.charges_amount || 0);
            //totMora += 0; // Cambiar si tienes el campo de mora en transacciones
          }


          // Control de salto de página
          if (y > pageHeight - 40) {
            doc.addPage();
            drawHeader(doc, { title: 'ESTADO DE CUENTA INTEGRAL (CONT.)', logo: logoImg, useColors: false });
            y = 45;
            doc.setFont('helvetica', 'bold');
            doc.setDrawColor(0, 0, 0);
            doc.setLineWidth(0.3);
            doc.line(margin, y, pageWidth - margin, y);
            doc.line(margin, y + 7, pageWidth - margin, y + 7);

            // Reutilizamos las mismas columnas declaradas arriba
            doc.text('FECHA', colFecha, y + 5);
            doc.text('N° TRANS.', colTrans, y + 5);
            doc.text('TIPO', colTipo, y + 5);
            doc.text('MONTO', colMonto, y + 5, { align: 'right' });
            doc.text('CAPITAL', colCapital, y + 5, { align: 'right' });
            doc.text('INTERÉS', colInteres, y + 5, { align: 'right' });
            doc.text('I. MORA', colMora, y + 5, { align: 'right' });
            doc.text('CARGOS', colCargos, y + 5, { align: 'right' });
            doc.text('SALDO', colSaldo, y + 5, { align: 'right' });

            y += 11;
            doc.setFont('helvetica', 'normal');
          }

          const shortDate = tx.transaction_date ? tx.transaction_date.split(' ')[0] : '-';
          const shortId = tx.id ? tx.id.toString().substring(0, 8) : '-';

          doc.text(shortDate, colFecha, y);
          doc.text(shortId, colTrans, y);

          const tipoTraducido = translateTxType(tx.transaction_type);
          doc.text(tipoTraducido || '-', colTipo, y);

          doc.text(formatMoney(tx.total_amount), colMonto, y, { align: 'right' });
          doc.text(formatMoney(tx.principal_applied), colCapital, y, { align: 'right' });
          doc.text(formatMoney(tx.interest_applied), colInteres, y, { align: 'right' });
          doc.text(formatMoney(tx.late_fee_applied), colMora, y, { align: 'right' });
          doc.text(formatMoney(tx.charges_amount), colCargos, y, { align: 'right' });

          doc.setFont('helvetica', 'bold');
          doc.text(formatMoney(tx.balance_after), colSaldo, y, { align: 'right' });
          doc.setFont('helvetica', 'normal');

          doc.setDrawColor(230, 230, 230);
          doc.setLineWidth(0.1);
          doc.line(margin, y + 2, pageWidth - margin, y + 2);

          y += 6;
        });
      }

      // --- FILA DE TOTALES GLOBALES ---
      doc.setFont('helvetica', 'bold');
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.3);
      doc.line(margin, y, pageWidth - margin, y);
      y += 5;

      // Totales cuadrados usando las variables globales
      doc.text('TOTALES:', colTipo + 20, y, { align: 'right' });
      doc.text(formatMoney(totMonto), colMonto, y, { align: 'right' });
      doc.text(formatMoney(totCapital), colCapital, y, { align: 'right' });
      doc.text(formatMoney(totInteres), colInteres, y, { align: 'right' });
      doc.text(formatMoney(totMora), colMora, y, { align: 'right' });
      doc.text(formatMoney(totCargos), colCargos, y, { align: 'right' });

      doc.line(margin, y + 2, pageWidth - margin, y + 2);
      doc.setLineWidth(0.1);
      doc.line(margin, y + 3, pageWidth - margin, y + 3);

      // 6. FOOTER GLOBAL
      drawFooter(doc, { companyName: config.trade_name });

      doc.save(`Estado_Cuenta_${loan.code}.pdf`);

    } catch (error) {
      console.error('Error generando estado de cuenta:', error);
    }
  };

  return { generateStatement };
}